/**
 * Single source of truth for derived Relief Status and Rebuilding Status.
 *
 * Used by:
 *  - Home Details (`?tab=home`) — live form values, via relief/rebuilding status displays
 *  - Homeowners filter-search list — enriched in `useFilterSearchHomes`
 *  - Reports — reads the same enriched fields from `useFilterSearchHomes`
 *
 * Both statuses use the same "worst status" rule across active work scopes,
 * ignoring scopes claimed by the Homeowner.
 *
 * Input shape: paths are rooted at `home.*`, e.g. `{ home: { roofingInfo: { roofing: "75%" } } }`.
 */

export type WorkscopeDisplayStatus =
  | "Not Started"
  | "In Progress"
  | "Complete"
  | "No Assistance Required"
  | "Unknown"

export type ReliefRebuildingStatusResult = {
  displayLabel: WorkscopeDisplayStatus
  colorClass: string
}

type StatusClaimedByPath = {
  statusPath: string
  claimedByPath?: string
  workScopeSlug: string
}

// Relief work scopes. Assessment is excluded so Relief reflects actual relief work only.
const RELIEF_STATUS_PATHS: StatusClaimedByPath[] = [
  {
    statusPath: "home.treeDebrisInfo.heavyEquipmentNeeded",
    claimedByPath: "home.treeDebrisInfo.heavyClaimedBy",
    workScopeSlug: "heavy-equipment-demo",
  },
  {
    statusPath: "home.treeDebrisInfo.treeWorkNeeded",
    claimedByPath: "home.treeDebrisInfo.treeClaimedBy",
    workScopeSlug: "tree-work",
  },
  {
    statusPath: "home.treeDebrisInfo.exteriorDebrisRemovalNeeded",
    claimedByPath: "home.treeDebrisInfo.debrisClaimedBy",
    workScopeSlug: "exterior-debris-removal",
  },
  {
    statusPath: "home.roofingInfo.roofTarpNeeded",
    claimedByPath: "home.roofingInfo.roofTarpClaimedBy",
    workScopeSlug: "tarp-roof",
  },
  {
    statusPath: "home.crawlSpaceInfo.crawlSpaceMuck",
    claimedByPath: "home.crawlSpaceInfo.crawlSpaceMuckClaimedBy",
    workScopeSlug: "m-and-g-crawl-space",
  },
  {
    statusPath: "home.crawlSpaceInfo.crawlSpaceInsulation",
    claimedByPath: "home.crawlSpaceInfo.crawlSpaceInsulationClaimedBy",
    workScopeSlug: "insulate-crawl-space",
  },
  {
    statusPath: "home.muckGutInfo.muckGutNeeded",
    claimedByPath: "home.muckGutInfo.claimedBy",
    workScopeSlug: "muck-and-gut-interior",
  },
  {
    statusPath: "home.moldRemediationInfo.moldRemediation",
    claimedByPath: "home.moldRemediationInfo.claimedBy",
    workScopeSlug: "mold-remediation",
  },
]

// Rebuilding work scopes, listed in Home Details page order for readability.
// Assessment is excluded. Order does not affect the result (worst status wins).
const REBUILDING_STATUS_PATHS: StatusClaimedByPath[] = [
  {
    statusPath: "home.rebuildPrepInfo.rebuildPrep",
    claimedByPath: "home.rebuildPrepInfo.claimedBy",
    workScopeSlug: "rebuild-prep",
  },
  {
    statusPath: "home.roughCarpentryInfo.roughCarpentry",
    claimedByPath: "home.roughCarpentryInfo.claimedBy",
    workScopeSlug: "rough-carpentry",
  },
  {
    statusPath: "home.electricalInfo.electrical",
    claimedByPath: "home.electricalInfo.claimedBy",
    workScopeSlug: "electrical-fixtures",
  },
  {
    statusPath: "home.electricalInfo.electricalRoughIn",
    claimedByPath: "home.electricalInfo.electricalRoughInClaimedBy",
    workScopeSlug: "rough-electrical",
  },
  {
    statusPath: "home.hvacInfo.hvac",
    claimedByPath: "home.hvacInfo.claimedBy",
    workScopeSlug: "hvac-final",
  },
  {
    statusPath: "home.hvacInfo.mechanicalRoughIn",
    claimedByPath: "home.hvacInfo.mechanicalRoughInClaimedBy",
    workScopeSlug: "rough-mechanical",
  },
  {
    statusPath: "home.plumbingInfo.plumbing",
    claimedByPath: "home.plumbingInfo.claimedBy",
    workScopeSlug: "plumbing-fixtures",
  },
  {
    statusPath: "home.plumbingInfo.plumbingRoughIn",
    claimedByPath: "home.plumbingInfo.plumbingRoughInClaimedBy",
    workScopeSlug: "rough-plumbing",
  },
  {
    statusPath: "home.roofingInfo.roofing",
    claimedByPath: "home.roofingInfo.roofingClaimedBy",
    workScopeSlug: "install-roofing",
  },
  {
    statusPath: "home.insulationInstallInfo.insulationInstall",
    claimedByPath: "home.insulationInstallInfo.claimedBy",
    workScopeSlug: "insulation",
  },
  {
    statusPath: "home.drywallHangingInfo.drywallHanging",
    claimedByPath: "home.drywallHangingInfo.claimedBy",
    workScopeSlug: "drywall-hanging",
  },
  {
    statusPath: "home.mudAndTapeInfo.mudAndTape",
    claimedByPath: "home.mudAndTapeInfo.claimedBy",
    workScopeSlug: "mud-and-tape",
  },
  {
    statusPath: "home.blockWallInfo.blockWallRepair",
    claimedByPath: "home.blockWallInfo.claimedBy",
    workScopeSlug: "block-wall",
  },
  {
    statusPath: "home.stuccoInfo.stucco",
    claimedByPath: "home.stuccoInfo.claimedBy",
    workScopeSlug: "stucco",
  },
  {
    statusPath: "home.bathroomRebuildInfo.bathroomRebuild",
    claimedByPath: "home.bathroomRebuildInfo.claimedBy",
    workScopeSlug: "bathroom-rebuild",
  },
  {
    statusPath: "home.cabinetInstallInfo.cabinetInstall",
    claimedByPath: "home.cabinetInstallInfo.cabinetClaimedBy",
    workScopeSlug: "install-cabinets",
  },
  {
    statusPath: "home.cabinetInstallInfo.counterInstall",
    claimedByPath: "home.cabinetInstallInfo.counterClaimedBy",
    workScopeSlug: "install-counters",
  },
  {
    statusPath: "home.cabinetInstallInfo.applianceInstall",
    claimedByPath: "home.cabinetInstallInfo.applianceClaimedBy",
    workScopeSlug: "install-appliances",
  },
  {
    statusPath: "home.interiorPaintInfo.interiorPaint",
    claimedByPath: "home.interiorPaintInfo.claimedBy",
    workScopeSlug: "interior-paint",
  },
  {
    statusPath: "home.flooringInstallInfo.flooringInstall",
    claimedByPath: "home.flooringInstallInfo.claimedBy",
    workScopeSlug: "flooring-install",
  },
  {
    statusPath: "home.installInteriorDoorsInfo.installInteriorDoors",
    claimedByPath: "home.installInteriorDoorsInfo.claimedBy",
    workScopeSlug: "install-interior-doors",
  },
  {
    statusPath: "home.trimInstallInfo.trimInstall",
    claimedByPath: "home.trimInstallInfo.claimedBy",
    workScopeSlug: "trim-install",
  },
  {
    statusPath: "home.exteriorDoorInstallInfo.exteriorDoorInstall",
    claimedByPath: "home.exteriorDoorInstallInfo.claimedBy",
    workScopeSlug: "install-exterior-doors",
  },
  {
    statusPath: "home.drivewayRepair.drivewayRepair",
    claimedByPath: "home.drivewayRepair.drivewayClaimedBy",
    workScopeSlug: "repair-driveway",
  },
  {
    statusPath: "home.stormShutters.shutterInstall",
    claimedByPath: "home.stormShutters.shuttersClaimedBy",
    workScopeSlug: "shutter-install",
  },
]

function getNestedValue(obj: unknown, path: string): unknown {
  return path.split(".").reduce((current: unknown, key) => (current as Record<string, unknown>)?.[key], obj)
}

// Lower priority number = worse status, and worse status wins.
function mapRawToDisplay(raw: string | null | undefined): { priority: number; result: ReliefRebuildingStatusResult } | null {
  if (!raw || typeof raw !== "string") return null
  const s = raw.trim()
  if (!s) return null

  if (s === "Needed Not Started") {
    return { priority: 1, result: { displayLabel: "Not Started", colorClass: "bg-gray-100 border-gray-300 text-gray-800" } }
  }
  if (["75%", "50%", "25%", "Materials Only"].includes(s)) {
    return { priority: 2, result: { displayLabel: "In Progress", colorClass: "bg-yellow-100 border-yellow-300 text-yellow-800" } }
  }
  if (s === "Unknown") {
    return { priority: 3, result: { displayLabel: "Unknown", colorClass: "bg-gray-100 border-gray-300 text-gray-800" } }
  }
  if (["Complete", "Materials Only Complete"].includes(s)) {
    return { priority: 4, result: { displayLabel: "Complete", colorClass: "bg-green-100 border-green-300 text-green-800" } }
  }
  if (s === "No Assistance Required") {
    return { priority: 5, result: { displayLabel: "No Assistance Required", colorClass: "bg-green-100 border-green-300 text-green-800" } }
  }
  return null
}

// When no statuses are set (all blank), show Not Started for Relief and Rebuilding
const BLANK_DEFAULT_RESULT: ReliefRebuildingStatusResult = {
  displayLabel: "Not Started",
  colorClass: "bg-gray-100 border-gray-300 text-gray-800",
}

const NO_ASSISTANCE_RESULT: ReliefRebuildingStatusResult = {
  displayLabel: "No Assistance Required",
  colorClass: "bg-green-100 border-green-300 text-green-800",
}

function claimedByIncludesHomeowner(raw: unknown): boolean {
  if (raw == null) return false
  if (Array.isArray(raw)) return raw.some(claimedByIncludesHomeowner)
  if (typeof raw !== "string") return false

  const value = raw.trim()
  if (!value) return false
  if (value === "Homeowner" || value === "Homeowner Scopes") return true

  try {
    return claimedByIncludesHomeowner(JSON.parse(value))
  } catch {
    return false
  }
}

function isWorkScopeActive(
  activeWorkScopes: Record<string, boolean> | undefined,
  workScopeSlug: string,
): boolean {
  return activeWorkScopes?.[workScopeSlug] !== false
}

/**
 * Worst status across the given work scopes, skipping inactive scopes, blank statuses,
 * and scopes claimed by the Homeowner.
 */
function computeWorstStatus(
  formValues: unknown,
  paths: StatusClaimedByPath[],
  activeWorkScopes?: Record<string, boolean>,
): ReliefRebuildingStatusResult {
  let worst: { priority: number; result: ReliefRebuildingStatusResult } | null = null
  let anyStatusFound = false
  let anyIncludedStatusFound = false

  if (!paths.some(({ workScopeSlug }) => isWorkScopeActive(activeWorkScopes, workScopeSlug))) {
    return NO_ASSISTANCE_RESULT
  }

  for (const { statusPath, claimedByPath, workScopeSlug } of paths) {
    if (!isWorkScopeActive(activeWorkScopes, workScopeSlug)) continue
    const status = getNestedValue(formValues, statusPath) as string | null | undefined
    const mapped = mapRawToDisplay(status)
    if (!mapped) continue // skip blank or missing (inactive scope / N/A)
    anyStatusFound = true
    if (claimedByIncludesHomeowner(claimedByPath ? getNestedValue(formValues, claimedByPath) : null)) {
      continue
    }
    anyIncludedStatusFound = true
    if (worst === null || mapped.priority < worst.priority) {
      worst = mapped
    }
  }
  if (worst) return worst.result
  if (anyStatusFound && !anyIncludedStatusFound) return NO_ASSISTANCE_RESULT
  return BLANK_DEFAULT_RESULT
}

/**
 * Relief Status: worst status across relief work scopes.
 */
export function computeReliefStatus(
  formValues: unknown,
  activeWorkScopes?: Record<string, boolean>,
): ReliefRebuildingStatusResult {
  return computeWorstStatus(formValues, RELIEF_STATUS_PATHS, activeWorkScopes)
}

/**
 * Rebuilding Status: worst status across rebuilding work scopes (including Roofing).
 */
export function computeRebuildingStatus(
  formValues: unknown,
  activeWorkScopes?: Record<string, boolean>,
): ReliefRebuildingStatusResult {
  return computeWorstStatus(formValues, REBUILDING_STATUS_PATHS, activeWorkScopes)
}
